import type { ComponentType } from "react";

export type AgeGroup = "0-2" | "3-5" | "6-8" | "9-12";

export type StoryCategory =
  | "historical-figures"
  | "world-history"
  | "science-nature"
  | "space-astronomy"
  | "inventions-technology"
  | "geography-cultures"
  | "arts-music"
  | "life-skills"
  | "math-logic"
  | "health-body";

export interface StoryParagraph {
  text: string;
  interactiveElement?: string;
  highlight?: string;
}

export interface IllustrationProps {
  className?: string;
  animated?: boolean;
}

export interface StoryMeta {
  id: string;
  title: string;
  slug: string;
  ageGroup: AgeGroup;
  category: StoryCategory;
  description: string;
  readingTime: number;
  moral: string;
  tags: string[];
  premium?: boolean;
}

export interface Story extends StoryMeta {
  paragraphs: StoryParagraph[];
  component: ComponentType;
  illustration?: ComponentType<IllustrationProps>;
  background?: ComponentType<IllustrationProps>;
}

export interface CategoryInfo {
  id: StoryCategory;
  label: string;
  icon: string;
  description: string;
}

export const CATEGORIES: CategoryInfo[] = [
  { id: "historical-figures", label: "Historical Figures", icon: "crown", description: "Meet the people who changed the world" },
  { id: "world-history", label: "World History", icon: "globe", description: "Explore fascinating moments from history" },
  { id: "science-nature", label: "Science & Nature", icon: "leaf", description: "Discover how our world works" },
  { id: "space-astronomy", label: "Space & Astronomy", icon: "star", description: "Journey through the cosmos" },
  { id: "inventions-technology", label: "Inventions & Technology", icon: "lightbulb", description: "Innovations that shaped our world" },
  { id: "geography-cultures", label: "Geography & Cultures", icon: "map", description: "Travel the world from your bedroom" },
  { id: "arts-music", label: "Arts & Music", icon: "music", description: "Celebrate creativity and expression" },
  { id: "life-skills", label: "Life Skills & Emotions", icon: "heart", description: "Learn about feelings and growing up" },
  { id: "math-logic", label: "Math & Logic", icon: "puzzle", description: "Patterns, numbers, and puzzles" },
  { id: "health-body", label: "Health & Body", icon: "apple", description: "Take care of your amazing body" },
];

export const AGE_GROUPS: { id: AgeGroup; label: string; description: string; color: string }[] = [
  { id: "0-2", label: "Baby & Toddler", description: "Simple, rhythmic stories with bold visuals", color: "from-red-400 to-yellow-400" },
  { id: "3-5", label: "Preschool", description: "Fairy tale adventures with gentle lessons", color: "from-pink-300 to-purple-300" },
  { id: "6-8", label: "Early Reader", description: "Historical adventures with real events", color: "from-blue-500 to-emerald-500" },
  { id: "9-12", label: "Pre-Teen", description: "Rich narratives with complex themes", color: "from-teal-600 to-indigo-600" },
];
